/**
 * Web Push subscription helpers.
 *
 * iOS 16.4+: Push only works when the site is installed via "Add to Home Screen".
 * The permission prompt MUST be triggered by a user gesture (button click).
 */
import { pushPublicKey, pushSubscribe, pushUnsubscribe } from "./api";

export function isPushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

/** iOS treats every PWA tab as a fresh process; window.matchMedia is the
 *  cleanest "am I running standalone (installed)?" check. */
export function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        // iOS Safari pre-spec flag
        window.navigator.standalone === true
    );
}

export function isIOS() {
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

/** Register /service-worker.js exactly once. Idempotent. */
export async function ensureSWRegistered() {
    if (!("serviceWorker" in navigator)) return null;
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) return existing;
    return navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
}

/** Returns the current PushSubscription or null. */
export async function getCurrentSubscription() {
    const reg = await ensureSWRegistered();
    if (!reg) return null;
    return reg.pushManager.getSubscription();
}

/** Full enable flow: request permission → subscribe → POST to backend.
 *  MUST be called from inside a user gesture handler. */
export async function enablePush() {
    if (!isPushSupported()) {
        throw new Error("Push notifications are not supported in this browser.");
    }
    if (isIOS() && !isStandalone()) {
        throw new Error(
            "On iOS, first tap Share → Add to Home Screen, then open the installed app and try again."
        );
    }
    const reg = await ensureSWRegistered();
    if (!reg) throw new Error("Service worker registration failed.");

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        throw new Error("Notification permission was not granted.");
    }

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        const { data } = await pushPublicKey();
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
    }
    await pushSubscribe(sub.toJSON());
    return sub;
}

export async function disablePush() {
    const reg = await ensureSWRegistered();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;
    const endpoint = sub.endpoint;
    try { await sub.unsubscribe(); } catch (err) {
        console.warn("[push] unsubscribe at browser failed:", err);
    }
    try { await pushUnsubscribe(endpoint); } catch (err) {
        console.warn("[push] unsubscribe at server failed:", err);
    }
    return true;
}
